import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import crm, { apiErr } from "./crmApi";

function AuthCard({ title, sub, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-pbs-bg p-6">
      <div className="w-full max-w-sm bg-white rounded-2xl border border-pbs-line p-7 pbs-shadow">
        <Link to="/crm/login" className="text-xs font-bold text-pbs-purple">← Back to sign in</Link>
        <h2 className="text-2xl font-extrabold text-pbs-dark mt-3">{title}</h2>
        <p className="text-sm text-pbs-grey mt-1 mb-5">{sub}</p>
        {children}
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await crm.forgot(email); setSent(true); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <AuthCard title="Reset your password" sub="Enter your employee email. If the account exists, a reset link will be sent.">
      {sent ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800" data-testid="forgot-sent">
          If the account exists, a reset link is on its way. It expires in 1 hour.
        </div>
      ) : (
        <form onSubmit={submit}>
          <label className="block text-xs font-semibold text-pbs-dark/70">Email</label>
          <input required type="email" data-testid="forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} className="crm-in mt-1" placeholder="you@plotbysquare.com" />
          <button data-testid="forgot-submit" disabled={busy} className="mt-4 w-full py-3 pbs-gradient text-white font-bold rounded-lg disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 size={16} className="animate-spin" />} Send Reset Link
          </button>
        </form>
      )}
    </AuthCard>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try { await crm.reset(token, pw); toast.success("Password updated. Please sign in."); nav("/crm/login"); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  if (!token) return <AuthCard title="Invalid link" sub="This reset link is missing its token. Request a new one from the sign-in page.">{null}</AuthCard>;
  return (
    <AuthCard title="Choose a new password" sub="Minimum 8 characters.">
      <form onSubmit={submit}>
        <label className="block text-xs font-semibold text-pbs-dark/70">New password</label>
        <input required type="password" data-testid="reset-pw" value={pw} onChange={(e) => setPw(e.target.value)} className="crm-in mt-1" />
        <label className="block mt-3 text-xs font-semibold text-pbs-dark/70">Confirm password</label>
        <input required type="password" data-testid="reset-pw2" value={pw2} onChange={(e) => setPw2(e.target.value)} className="crm-in mt-1" />
        <button data-testid="reset-submit" disabled={busy} className="mt-4 w-full py-3 pbs-gradient text-white font-bold rounded-lg disabled:opacity-60 flex items-center justify-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />} Update Password
        </button>
      </form>
    </AuthCard>
  );
}
